'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { styled } from '@components-shared/src/stitches';
import { useScrollPosition } from '@components/hooks/useScrollPosition';
import { Sheet } from '@components/main-components/sheet/Sheet';
import { MediaQuery } from '@context/mediaQuery';
import { useChartCardContext } from './Context';

const TRANSITION = { type: 'spring', damping: 30, stiffness: 300, duration: 0.1 };
const HEADER_HEIGHT = 94;

export function ChartCardAccordionDetails({
  children,
  trackTitle,
  artistName,
}: {
  children: React.ReactNode;
  trackTitle?: JSX.Element;
  artistName?: JSX.Element;
}) {
  const { tablet } = MediaQuery.useContainer();
  const { expandedId, setExpandedId } = useChartCardContext();
  const [isDraggingContent, setIsDraggingContent] = React.useState(false);
  const handleContentOnTouchStart = React.useCallback(() => {
    setIsDraggingContent(true);
  }, []);

  const handleContentOnTouchEnd = React.useCallback(() => {
    setIsDraggingContent(false);
  }, []);

  const [scrollPosition, scrollProps] = useScrollPosition();

  // We need to check for tablet here, and can't use Tailwind to hide mobile sheet from tablet and up
  // Not sure why, but it causes the mobile sheet to still show on tablet
  // Likely the sheet lib is doing some dom manipulation that overwrites or replaces tailwind styles
  return tablet ? (
    <div className="hidden md:block">
      <DesktopContainer layout transition={TRANSITION}>
        {children}
      </DesktopContainer>
    </div>
  ) : (
    <Sheet
      disableDrag={isDraggingContent}
      isOpen={!!expandedId}
      onClose={() => setExpandedId?.(null)}
    >
      <Sheet.Content hasThumb>
        <TitleSection elevation={!scrollPosition.isScrolledToTop}>
          <Title>{trackTitle}</Title>
          <SubTitle>{artistName}</SubTitle>
        </TitleSection>
        <MobileContainer
          onTouchStart={handleContentOnTouchStart}
          onTouchEnd={handleContentOnTouchEnd}
          {...scrollProps}
        >
          {children}
        </MobileContainer>
      </Sheet.Content>
    </Sheet>
  );
}

// Separate styles so mobile container doesn't have to be a motion div
const SharedContainerStyles = {
  position: 'relative',
  display: 'flex',
  background: '$base50',
  gap: 40,
  padding: '16px 20px',
  borderRadius: '0 0 $large $large',
  overflow: 'hidden',
  flexDirection: 'column',

  '@tablet': {
    padding: 24,
  },
};

const DesktopContainer = styled(motion.div, {
  ...SharedContainerStyles,
  // Add margin to avoid connecting accordion background and next card hover state
  marginBottom: 20,
});

const MobileContainer = styled('div', {
  ...SharedContainerStyles,
  gap: 0,
  padding: `0 16px 20px`,
  background: '$white',
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: `calc(100% - ${HEADER_HEIGHT}px)`,
});

const TitleSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  paddingTop: 28,
  paddingBottom: 20,
  paddingX: 16,
  height: HEADER_HEIGHT,
  transition: 'all 0.2s ease-in-out',
  zIndex: '$above1', // Lift element up so we can see box-shadow
  position: 'relative',

  variants: {
    elevation: {
      true: {
        shadow: 'M',
      },
    },
  },
});

const Title = styled('h2', {
  title: 'L',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const SubTitle = styled('h3', {
  base: 'S',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const SectionHeader = styled('h4', {
  title: 'S',
  margin: '20px 0 12px',

  '@tablet': {
    title: 'M',
    marginBottom: 12,
  },
});

export const EmptySection = styled('div', {
  base: 'L',
  margin: '20px 0',
  color: '$base500',

  a: {
    textDecoration: 'underline',
    color: '$base800',
    fontWeight: 600,
  },
});

ChartCardAccordionDetails.SectionHeader = SectionHeader;
